import { useChatContext } from './AIChatProvider';
import { Card, Dropdown, Nav } from 'react-bootstrap';
import classNames from 'classnames';
import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Conversation } from './DataDefinitions';
import RevealDropdown, { RevealDropdownTrigger } from 'components/base/RevealDropdown';
import Button from 'components/base/Button';
import { useTranslation } from 'react-i18next';

const ActionDropdownItems = ({ conversaction_id, onDelete }: {
    conversaction_id: string | number,
    onDelete: (conversaction_id: string | number) => void;
}) => {

    const { t } = useTranslation();
    return (
        <>
            <Dropdown.Item eventKey="1" onClick={() => onDelete(conversaction_id)} className="text-danger">
                {t("remove")}
            </Dropdown.Item>
        </>
    );
};

const AIConversationListitem = ({ conversation }: { conversation: Conversation }) => {
    const { currentConversation, setCurrentConversation, deleteConversation } = useChatContext();

    /*
    const lastMessage = useMemo(
      () => conversation.messages[conversation.messages.length - 1],
      [conversation]
    );
    const unseenMessageCount = useMemo(
      () =>
        conversation.messages.filter(
          message => message.type === 'received' && !message.readAt
        ).length,
      [conversation]
    );
  
    const markedAsRead = () => {
      chatDispatch({
        type: MARKED_AS_READ,
        payload: { conversationId: conversation.id }
      });
    };
    */

    const selectConversation = () => {
        setCurrentConversation(conversation);
    };

    return (
        <Nav.Item key={conversation.conversation_id} className="ai-chat-item mt-1 mb-1" >
            <Nav.Link
                as={Link}
                to={"#"} style={{ 'cursor': 'default' }}
                onClick={selectConversation}
                className={classNames(
                    'd-flex align-items-center justify-content-center p-2',
                    {
                        active: currentConversation?.conversation_id === conversation.conversation_id
                    }
                )}
            >
                <div className="flex-1 d-sm-none d-xl-block">
                    <div className="d-flex justify-content-between align-items-center">

                        <h5 className="text-900 fw-normal name text-nowrap">
                            {conversation.title}
                        </h5>

                        <RevealDropdownTrigger>
                            <RevealDropdown>
                                <ActionDropdownItems conversaction_id={conversation.conversation_id} onDelete={() => { deleteConversation(conversation.conversation_id); }} />
                            </RevealDropdown>
                        </RevealDropdownTrigger>
                    </div>
                </div>
            </Nav.Link>
        </Nav.Item>
    );
};

const AIChatSidebar = ({ className }: { className?: string }) => {

    const { t } = useTranslation();
    const { startNewChat, conversations } = useChatContext();

    // Utility function to get the start of the current month
    const startOfCurrentMonth = () => {
        const now = new Date();
        return new Date(now.getFullYear(), now.getMonth(), 1);
    };

    // Utility function to get the start of the previous month
    const startOfPreviousMonth = () => {
        const now = new Date();
        return new Date(now.getFullYear(), now.getMonth() - 1, 1);
    };

    // Utility function to get 7 days ago
    const sevenDaysAgo = () => {
        const now = new Date();
        return new Date(now.setDate(now.getDate() - 7));
    };

    // Function to categorize the items
    const groupItemsByTime = (items: Conversation[]) => {
        const now = new Date();
        const lastSevenDays: Conversation[] = [];
        const currentMonth: Conversation[] = [];
        const previousMonth: Conversation[] = [];
        const older: Conversation[] = [];

        items.forEach(item => {
            const itemDate = new Date(item.inserted_at);
            if (itemDate >= sevenDaysAgo()) {
                lastSevenDays.push(item);
            } else if (itemDate >= startOfCurrentMonth()) {
                currentMonth.push(item);
            } else if (itemDate >= startOfPreviousMonth()) {
                previousMonth.push(item);
            } else {
                older.push(item);
            }
        });

        return {
            lastSevenDays,
            currentMonth,
            previousMonth,
            older,
        };
    };

    // Group items using useMemo to avoid recalculating on each render
    const groupedItems = useMemo(() => groupItemsByTime(conversations), [conversations]);

    return (
        <>
            <Card className={classNames(className, 'chat-sidebar p-3 p-xl-1')} >

                <Button variant="outline-primary"
                    className="mb-4"
                    onClick={() => startNewChat()}
                    aria-label="Click to submit the form"
                >
                    {t("AIChatSidebar.new_chat")}
                </Button>

                <div className="scrollbar">
                    <Nav className="chat-conversation-tab flex-column">

                        {/* Last Seven Days Section */}
                        {groupedItems.lastSevenDays.length > 0 && <h5>{t("AIChatSidebar.last_seven_days")}</h5>}

                        {groupedItems.lastSevenDays.length > 0 && (
                            groupedItems.lastSevenDays.map(conversation => (
                                <AIConversationListitem conversation={conversation} key={conversation.conversation_id} />
                            ))
                        )}

                        {/* Current Month Section */}
                        {groupedItems.currentMonth.length > 0 && <h5>{t("AIChatSidebar.current_month")}</h5>}

                        {groupedItems.currentMonth.length > 0 && (
                            groupedItems.currentMonth.map(conversation => (
                                <AIConversationListitem conversation={conversation} key={conversation.conversation_id} />
                            ))
                        )}

                        {/* Previous Month Section */}
                        {groupedItems.previousMonth.length > 0 && <h5>{t("AIChatSidebar.previous_month")}</h5>}

                        {groupedItems.previousMonth.length > 0 && (
                            groupedItems.previousMonth.map(conversation => (
                                <AIConversationListitem conversation={conversation} key={conversation.conversation_id} />
                            ))
                        )}

                        {/* Older Section */}
                        {groupedItems.older.length > 0 && <h5>{t("AIChatSidebar.older")}</h5>}

                        {groupedItems.older.length > 0 && (
                            groupedItems.older.map(conversation => (
                                <AIConversationListitem conversation={conversation} key={conversation.conversation_id} />
                            ))
                        )}
                    </Nav>
                </div>
            </Card>

        </>
    );
};

export default AIChatSidebar;
